// EmailLegalHolds.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Shield,
  Plus,
  Search,
  AlertCircle,
  CheckCircle,
  XCircle,
  Download,
  Clock,
  FileText,
  ChevronDown,
} from "lucide-react";
import api from "../../services/api";

const EmailLegalHolds = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [holds, setHolds] = useState([]);
  const [filteredHolds, setFilteredHolds] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [selectedHold, setSelectedHold] = useState(null);
  const [filterActive, setFilterActive] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const [createForm, setCreateForm] = useState({
    messageId: "",
    holdReason: "litigation",
    caseReference: "",
    expiresAt: "",
    notes: "",
  });

  const [removeForm, setRemoveForm] = useState({
    removalNotes: "",
  });

  useEffect(() => {
    loadHolds();
  }, []);

  useEffect(() => {
    filterHolds();
  }, [holds, filterActive, searchQuery]);

  const loadHolds = async () => {
    setLoading(true);
    try {
      const response = await api.getEmailLegalHolds({});
      setHolds(response || []);
    } catch (error) {
      console.error("Failed to load legal holds:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterHolds = () => {
    let filtered = [...holds];

    if (filterActive === "active") {
      filtered = filtered.filter((h) => h.isActive);
    } else if (filterActive === "inactive") {
      filtered = filtered.filter((h) => !h.isActive);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (h) =>
          h.caseReference?.toLowerCase().includes(query) ||
          h.message?.subject?.toLowerCase().includes(query) ||
          h.notes?.toLowerCase().includes(query)
      );
    }

    setFilteredHolds(filtered);
  };

  const handleCreateHold = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        ...createForm,
        expiresAt: createForm.expiresAt
          ? new Date(createForm.expiresAt).toISOString()
          : null,
      };

      await api.createEmailLegalHold(payload);
      setShowCreateModal(false);
      setCreateForm({
        messageId: "",
        holdReason: "litigation",
        caseReference: "",
        expiresAt: "",
        notes: "",
      });
      loadHolds();
    } catch (error) {
      console.error("Failed to create legal hold:", error);
    }
  };

  const handleRemoveHold = async (e) => {
    e.preventDefault();

    try {
      await api.removeEmailLegalHold(selectedHold.id, removeForm);
      setShowRemoveModal(false);
      setSelectedHold(null);
      setRemoveForm({ removalNotes: "" });
      loadHolds();
    } catch (error) {
      console.error("Failed to remove legal hold:", error);
    }
  };

  const handleExport = async (holdId) => {
    try {
      const response = await api.exportEmailLegalHold(holdId);

      const blob = new Blob([JSON.stringify(response, null, 2)], {
        type: "application/json",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `legal-hold-${holdId}-${new Date().toISOString()}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Failed to export legal hold:", error);
    }
  };

  const getReasonBadge = (reason) => {
    const config = {
      litigation: {
        bg: "bg-red-500/10",
        text: "text-red-400",
        label: "Litigation",
      },
      hr_investigation: {
        bg: "bg-orange-500/10",
        text: "text-orange-400",
        label: "HR Investigation",
      },
      compliance: {
        bg: "bg-blue-500/10",
        text: "text-blue-400",
        label: "Compliance",
      },
      audit: { bg: "bg-gray-500/10", text: "text-gray-400", label: "Audit" },
      other: { bg: "bg-gray-500/10", text: "text-gray-400", label: "Other" },
    };
    return config[reason] || config.other;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0C0E12] text-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0C0E12] text-white font-inter">
      <div className="p-4 lg:p-6">
        {/* Header */}
        <div className="mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-1 sm:mb-2 text-white flex items-center gap-3">
              <Shield className="w-8 h-8 text-purple-500" />
              Legal Holds
            </h1>
            <p className="text-sm sm:text-base text-gray-400 leading-relaxed">
              Manage legal preservation of email communications
            </p>
          </div>

          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 text-white rounded-lg font-medium transition-all duration-300 relative hover:scale-[1.02] hover:shadow-xl text-sm sm:text-base min-w-0"
            style={{
              background:
                "radial-gradient(50% 50% at 50% 50%, #744EC5 0%, #5A3D9A 100%)",
              border: "1px solid",
              borderImageSource:
                "radial-gradient(50% 50% at 50% 50%, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)",
              boxShadow: `
                                                0px 36px 32px -16px #7F56D936,
                                                0px 8px 16px 0px #7F56D91F,
                                                0px 2px 4px -8px #7F56D914,
                                                0px 8px 24px -16px #FFFFFF3D inset,
                                                0px -24px 32px 0px #FFFFFF38 inset,
                                                0px 0px 12px 0px #FFFFFF inset
                                              `,
              borderImageSlice: "1",
            }}
          >
            <Plus className="w-5 h-5" />

            <span className="whitespace-nowrap">Place New Hold</span>
          </button>
        </div>

        {/* Filters */}
        <div className="bg-[#0C0E12] border border-gray-700 rounded-lg p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by case reference, subject, or notes..."
                className="w-full pl-10 pr-4 py-2 bg-[#13161B] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <Search className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setFilterActive("all")}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filterActive === "all"
                    ? "bg-purple-600 text-white"
                    : "bg-[#13161B] border border-gray-700 text-gray-400 hover:bg-gray-700"
                }`}
              >
                All ({holds.length})
              </button>
              <button
                onClick={() => setFilterActive("active")}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filterActive === "active"
                    ? "bg-purple-600 text-white"
                    : "bg-[#13161B] border border-gray-700 text-gray-400 hover:bg-gray-700"
                }`}
              >
                Active ({holds.filter((h) => h.isActive).length})
              </button>
              <button
                onClick={() => setFilterActive("inactive")}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filterActive === "inactive"
                    ? "bg-purple-600 text-white"
                    : "bg-[#13161B] border border-gray-700 text-gray-400 hover:bg-gray-700"
                }`}
              >
                Inactive ({holds.filter((h) => !h.isActive).length})
              </button>
            </div>
          </div>
        </div>

        {/* Holds List */}
        <div className="space-y-4">
          {filteredHolds.map((hold) => (
            <div
              key={hold.id}
              className="bg-[#0C0E12] border border-gray-700 rounded-lg p-6 hover:border-gray-600 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3 flex-wrap">
                    <h3 className="font-semibold text-white text-lg">
                      {hold.message?.subject || "(No Subject)"}
                    </h3>
                    {hold.isActive ? (
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-500/10 text-green-400 flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        Active
                      </span>
                    ) : (
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-500/10 text-gray-400 flex items-center gap-1">
                        <XCircle className="w-3 h-3" />
                        Inactive
                      </span>
                    )}
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium ${
                        getReasonBadge(hold.holdReason).bg
                      } ${getReasonBadge(hold.holdReason).text}`}
                    >
                      {getReasonBadge(hold.holdReason).label}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mb-4">
                    <div>
                      <span className="text-gray-500">Case Reference:</span>
                      <span className="ml-2 font-medium text-white">
                        {hold.caseReference || "N/A"}
                      </span>
                    </div>

                    <div>
                      <span className="text-gray-500">Placed By:</span>
                      <span className="ml-2 font-medium text-white">
                        {hold.placer?.email || "Unknown"}
                      </span>
                    </div>

                    <div>
                      <span className="text-gray-500">Placed At:</span>
                      <span className="ml-2 font-medium text-white">
                        {formatDate(hold.placedAt)}
                      </span>
                    </div>

                    {hold.expiresAt && (
                      <div>
                        <span className="text-gray-500">Expires At:</span>
                        <span className="ml-2 font-medium text-white">
                          {formatDate(hold.expiresAt)}
                        </span>
                      </div>
                    )}

                    {!hold.isActive && hold.removedAt && (
                      <>
                        <div>
                          <span className="text-gray-500">Removed By:</span>
                          <span className="ml-2 font-medium text-white">
                            {hold.remover?.email || "Unknown"}
                          </span>
                        </div>

                        <div>
                          <span className="text-gray-500">Removed At:</span>
                          <span className="ml-2 font-medium text-white">
                            {formatDate(hold.removedAt)}
                          </span>
                        </div>
                      </>
                    )}
                  </div>

                  {hold.notes && (
                    <div className="p-3 bg-[#13161B] border border-gray-700 rounded-lg mb-3">
                      <p className="text-sm text-gray-300">{hold.notes}</p>
                    </div>
                  )}

                  {!hold.isActive && hold.removalNotes && (
                    <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg mb-3">
                      <p className="text-sm font-medium text-red-400 mb-1">
                        Removal Notes:
                      </p>
                      <p className="text-sm text-red-300">
                        {hold.removalNotes}
                      </p>
                    </div>
                  )}

                  {hold.chainOfCustody?.length > 0 && (
                    <div className="mt-4">
                      <button
                        onClick={() => {
                          const details = document.getElementById(
                            `custody-${hold.id}`
                          );
                          details.classList.toggle("hidden");
                        }}
                        className="text-sm text-purple-400 hover:text-purple-300 font-medium flex items-center gap-2"
                      >
                        <ChevronDown className="w-4 h-4" />
                        View Chain of Custody ({hold.chainOfCustody.length}{" "}
                        events)
                      </button>
                      <div
                        id={`custody-${hold.id}`}
                        className="hidden mt-2 space-y-2"
                      >
                        {hold.chainOfCustody.map((event, index) => (
                          <div
                            key={index}
                            className="flex items-start gap-3 p-3 bg-[#13161B] border border-gray-700 rounded-lg"
                          >
                            <Clock className="w-4 h-4 text-gray-400 mt-1" />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-white">
                                  {event.action
                                    .replace(/_/g, " ")
                                    .replace(/\b\w/g, (l) => l.toUpperCase())}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {new Date(event.timestamp).toLocaleString()}
                                </span>
                              </div>
                              <p className="text-sm text-gray-400">
                                {event.details}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="ml-4 flex flex-col gap-2">
                  <button
                    onClick={() => handleExport(hold.id)}
                    className="flex items-center gap-2 px-3 py-2 bg-[#13161B] border border-gray-700 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors text-sm"
                  >
                    <Download className="w-4 h-4" />
                    Export
                  </button>

                  <button
                    onClick={() =>
                      navigate(`/email/messages/${hold.emailMessageId}`)
                    }
                    className="flex items-center gap-2 px-3 py-2 bg-[#13161B] border border-gray-700 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors text-sm"
                  >
                    <FileText className="w-4 h-4" />
                    View Email
                  </button>

                  {hold.isActive && (
                    <button
                      onClick={() => {
                        setSelectedHold(hold);
                        setShowRemoveModal(true);
                      }}
                      className="flex items-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
                    >
                      <XCircle className="w-4 h-4" />
                      Remove Hold
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredHolds.length === 0 && (
          <div className="text-center py-20">
            <Shield className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">No legal holds found</p>
          </div>
        )}

        {/* Create Hold Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div
              className="fixed inset-0 bg-black bg-opacity-75 transition-opacity"
              onClick={() => setShowCreateModal(false)}
            />

            <div className="flex min-h-full items-center justify-center p-4">
              <div className="relative w-full max-w-2xl transform rounded-lg bg-[#0C0E12] border border-gray-700 shadow-xl transition-all">
                <div className="flex items-center justify-between border-b border-gray-700 px-6 py-4">
                  <h3 className="text-lg font-semibold text-white">
                    Place Legal Hold
                  </h3>
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="rounded-lg p-1 text-gray-400 hover:bg-gray-700 hover:text-gray-300 transition-colors"
                  >
                    <XCircle className="w-5 h-5" />
                  </button>
                </div>

                <form
                  onSubmit={handleCreateHold}
                  className="px-6 py-4 space-y-4"
                >
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Message ID *
                    </label>
                    <input
                      type="text"
                      required
                      value={createForm.messageId}
                      onChange={(e) =>
                        setCreateForm({
                          ...createForm,
                          messageId: e.target.value,
                        })
                      }
                      placeholder="Enter email message ID"
                      className="w-full px-3 py-2 bg-[#13161B] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Navigate to the email and copy its ID from the URL
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Hold Reason *
                    </label>
                    <select
                      required
                      value={createForm.holdReason}
                      onChange={(e) =>
                        setCreateForm({
                          ...createForm,
                          holdReason: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 bg-[#13161B] border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="litigation">Litigation</option>
                      <option value="hr_investigation">HR Investigation</option>
                      <option value="compliance">Compliance</option>
                      <option value="audit">Audit</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Case Reference
                    </label>
                    <input
                      type="text"
                      value={createForm.caseReference}
                      onChange={(e) =>
                        setCreateForm({
                          ...createForm,
                          caseReference: e.target.value,
                        })
                      }
                      placeholder="e.g., CASE-2025-001"
                      className="w-full px-3 py-2 bg-[#13161B] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Expiration Date (Optional)
                    </label>
                    <input
                      type="datetime-local"
                      value={createForm.expiresAt}
                      onChange={(e) =>
                        setCreateForm({
                          ...createForm,
                          expiresAt: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 bg-[#13161B] border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Notes
                    </label>
                    <textarea
                      value={createForm.notes}
                      onChange={(e) =>
                        setCreateForm({ ...createForm, notes: e.target.value })
                      }
                      rows={4}
                      placeholder="Add any additional notes about this legal hold..."
                      className="w-full px-3 py-2 bg-[#13161B] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      type="submit"
                      className="flex-1 items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 text-white rounded-lg font-medium transition-all duration-300 relative hover:scale-[1.02] hover:shadow-xl text-sm sm:text-base min-w-0"
                      style={{
                        background:
                          "radial-gradient(50% 50% at 50% 50%, #744EC5 0%, #5A3D9A 100%)",
                        border: "1px solid",
                        borderImageSource:
                          "radial-gradient(50% 50% at 50% 50%, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)",
                        boxShadow: `
                                                0px 36px 32px -16px #7F56D936,
                                                0px 8px 16px 0px #7F56D91F,
                                                0px 2px 4px -8px #7F56D914,
                                                0px 8px 24px -16px #FFFFFF3D inset,
                                                0px -24px 32px 0px #FFFFFF38 inset,
                                                0px 0px 12px 0px #FFFFFF inset
                                              `,
                        borderImageSlice: "1",
                      }}
                    >
                      <span className="whitespace-nowrap">
                        Place Legal Hold
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowCreateModal(false)}
                      className="flex-1 px-4 py-2 bg-[#13161B] border border-gray-700 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Remove Hold Modal */}
        {showRemoveModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div
              className="fixed inset-0 bg-black bg-opacity-75 transition-opacity"
              onClick={() => {
                setShowRemoveModal(false);
                setSelectedHold(null);
              }}
            />

            <div className="flex min-h-full items-center justify-center p-4">
              <div className="relative w-full max-w-2xl transform rounded-lg bg-[#0C0E12] border border-gray-700 shadow-xl transition-all">
                <div className="flex items-center justify-between border-b border-gray-700 px-6 py-4">
                  <h3 className="text-lg font-semibold text-white">
                    Remove Legal Hold
                  </h3>
                  <button
                    onClick={() => {
                      setShowRemoveModal(false);
                      setSelectedHold(null);
                    }}
                    className="rounded-lg p-1 text-gray-400 hover:bg-gray-700 hover:text-gray-300 transition-colors"
                  >
                    <XCircle className="w-5 h-5" />
                  </button>
                </div>

                <form
                  onSubmit={handleRemoveHold}
                  className="px-6 py-4 space-y-4"
                >
                  <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-red-400 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-red-400">Warning</h4>
                        <p className="text-sm text-red-300 mt-1">
                          Removing a legal hold is a significant action. Please
                          ensure you have proper authorization and document the
                          reason for removal.
                        </p>
                      </div>
                    </div>
                  </div>

                  {selectedHold && (
                    <div className="p-3 bg-[#13161B] border border-gray-700 rounded-lg">
                      <p className="text-sm text-gray-300">
                        <strong>Case:</strong>{" "}
                        {selectedHold.caseReference || "N/A"}
                      </p>
                      <p className="text-sm text-gray-300">
                        <strong>Subject:</strong>{" "}
                        {selectedHold.message?.subject || "(No Subject)"}
                      </p>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Removal Notes *
                    </label>
                    <textarea
                      required
                      value={removeForm.removalNotes}
                      onChange={(e) =>
                        setRemoveForm({
                          ...removeForm,
                          removalNotes: e.target.value,
                        })
                      }
                      rows={4}
                      placeholder="Document the reason for removing this legal hold..."
                      className="w-full px-3 py-2 bg-[#13161B] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      type="submit"
                      className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                      Confirm Removal
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowRemoveModal(false);
                        setSelectedHold(null);
                      }}
                      className="flex-1 px-4 py-2 bg-[#13161B] border border-gray-700 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailLegalHolds;
